import { useEffect, useState, useMemo } from "react";
import ReactMarkdown from "react-markdown";
import { REMARK_PLUGINS } from "@/lib/markdown";
import { Link } from "react-router-dom";
import { Eye, Edit3, Trash2, Plus, ExternalLink, Sparkles, X, Clock, Calendar, List } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import ArticleCalendar from "@/components/admin/ArticleCalendar";
import ImageUploader from "@/components/ImageUploader";

const slugifyClient = (s) =>
  (s || "")
    .toLowerCase()
    .trim()
    .replace(/['']/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const EMPTY = {
  slug: "",
  title: "",
  excerpt: "",
  cover_image: "",
  content_md:
    "## Start here\n\nA short hook paragraph. What's the article about and why should the reader stay?\n\n### Section title\n\n- Bullet one\n- Bullet two\n\n**Bold for emphasis.** Add product links by mentioning the slug below in the *Related products* field — they'll appear at the bottom of the page automatically.",
  related_product_slugs: [],
  published: false,
  publish_at: "",
};

// Convert an ISO datetime string (UTC) into a value suitable for
// <input type="datetime-local"> (browser-local, no seconds, no TZ).
const isoToLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`;
};

// Convert "datetime-local" string back into a UTC ISO string.
const localInputToIso = (local) => {
  if (!local) return null;
  const d = new Date(local);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
};

// Status label for the sidebar: scheduled / live / draft.
const articleStatus = (a) => {
  if (!a.published) return { label: "draft", cls: "bg-gray-200 text-black" };
  if (a.publish_at && new Date(a.publish_at).getTime() > Date.now())
    return { label: "scheduled", cls: "pcs-bg-blue text-white" };
  return { label: "live", cls: "pcs-bg-pink text-white" };
};

export default function ArticlesPanel({ initialSlug }) {
  const [articles, setArticles] = useState([]);
  const [products, setProducts] = useState([]);
  const [selected, setSelected] = useState(null); // slug or null for "new"
  const [draft, setDraft] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [loading, setLoading] = useState(true);
  const [productSearch, setProductSearch] = useState("");

  // AI draft modal state
  const [aiOpen, setAiOpen] = useState(false);
  const [aiTopic, setAiTopic] = useState("");
  const [aiKeywords, setAiKeywords] = useState("");
  const [aiTone, setAiTone] = useState("fun, festive, conversational");
  const [aiTargetWords, setAiTargetWords] = useState(1000);
  const [aiBusy, setAiBusy] = useState(false);

  // "editor" (sidebar + editor) or "calendar" (month-grid)
  const [view, setView] = useState("editor");

  const loadArticles = () => api.get("/articles").then((r) => setArticles(r.data));

  useEffect(() => {
    setLoading(true);
    Promise.all([
      loadArticles(),
      api.get("/products", { params: { page_size: 200 } }).then((r) => setProducts(r.data.items)),
    ]).finally(() => setLoading(false));
  }, []);

  const isNew = selected === null;
  const original = useMemo(
    () => (selected ? articles.find((a) => a.slug === selected) : null),
    [articles, selected]
  );

  const dirty = useMemo(() => {
    if (isNew) {
      return Boolean(
        draft.title || draft.excerpt || draft.cover_image || draft.content_md !== EMPTY.content_md
      );
    }
    if (!original) return false;
    return (
      draft.title !== original.title ||
      draft.excerpt !== original.excerpt ||
      draft.cover_image !== original.cover_image ||
      draft.content_md !== original.content_md ||
      draft.published !== original.published ||
      isoToLocalInput(draft.publish_at) !== isoToLocalInput(original.publish_at) ||
      JSON.stringify(draft.related_product_slugs) !==
        JSON.stringify(original.related_product_slugs)
    );
  }, [draft, original, isNew]);

  const startNew = () => {
    setSelected(null);
    setDraft({ ...EMPTY });
    setShowPreview(false);
  };

  const openArticle = (slug) => {
    const a = articles.find((x) => x.slug === slug);
    if (!a) return;
    setSelected(slug);
    setDraft({
      slug: a.slug,
      title: a.title,
      excerpt: a.excerpt || "",
      cover_image: a.cover_image || "",
      content_md: a.content_md || "",
      related_product_slugs: a.related_product_slugs || [],
      published: a.published !== false,
      publish_at: isoToLocalInput(a.publish_at),
    });
    setShowPreview(false);
  };

  // Deep-link: open the article from `?slug=` once articles have loaded.
  useEffect(() => {
    if (!initialSlug || articles.length === 0) return;
    if (articles.some((a) => a.slug === initialSlug)) {
      openArticle(initialSlug);
      setView("editor");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialSlug, articles]);

  const save = async () => {
    if (!draft.title.trim() || !draft.content_md.trim()) {
      toast.error("Title and content are required");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        title: draft.title.trim(),
        excerpt: draft.excerpt.trim(),
        cover_image: draft.cover_image.trim(),
        content_md: draft.content_md,
        related_product_slugs: draft.related_product_slugs,
        published: draft.published,
        publish_at: localInputToIso(draft.publish_at),
      };
      if (isNew) {
        payload.slug = draft.slug.trim() || slugifyClient(draft.title);
        const { data } = await api.post("/articles", payload);
        toast.success(
          payload.publish_at && new Date(payload.publish_at).getTime() > Date.now()
            ? `Scheduled "${data.title}"`
            : data.published
            ? `Published "${data.title}"`
            : `Saved draft "${data.title}"`
        );
        await loadArticles();
        setSelected(data.slug);
        setDraft({
          slug: data.slug,
          title: data.title,
          excerpt: data.excerpt || "",
          cover_image: data.cover_image || "",
          content_md: data.content_md || "",
          related_product_slugs: data.related_product_slugs || [],
          published: data.published !== false,
          publish_at: isoToLocalInput(data.publish_at),
        });
      } else {
        // PUT — allow slug change
        if (draft.slug && draft.slug !== selected) {
          payload.slug = draft.slug;
        }
        const { data } = await api.put(`/articles/${selected}`, payload);
        toast.success(`Saved "${data.title}"`);
        await loadArticles();
        setSelected(data.slug);
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const del = async () => {
    if (isNew || !original) return;
    if (!window.confirm(`Delete "${original.title}"? This cannot be undone.`)) return;
    setBusy(true);
    try {
      await api.delete(`/articles/${original.slug}`);
      toast.success("Deleted");
      await loadArticles();
      startNew();
    } catch {
      toast.error("Delete failed");
    } finally {
      setBusy(false);
    }
  };

  const togglePublished = () =>
    setDraft((d) => ({ ...d, published: !d.published }));

  const toggleRelated = (slug) =>
    setDraft((d) => ({
      ...d,
      related_product_slugs: d.related_product_slugs.includes(slug)
        ? d.related_product_slugs.filter((s) => s !== slug)
        : [...d.related_product_slugs, slug],
    }));

  const filteredProducts = useMemo(() => {
    const q = productSearch.trim().toLowerCase();
    if (!q) return products;
    return products.filter(
      (p) => p.title.toLowerCase().includes(q) || p.slug.toLowerCase().includes(q)
    );
  }, [products, productSearch]);

  const generateWithAi = async () => {
    if (!aiTopic.trim()) {
      toast.error("Topic is required");
      return;
    }
    setAiBusy(true);
    try {
      const keywords = aiKeywords
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
      const { data } = await api.post("/admin/ai/article-draft", {
        topic: aiTopic.trim(),
        tone: aiTone,
        target_words: Number(aiTargetWords) || 1000,
        keywords,
        related_product_slugs: draft.related_product_slugs,
      });
      // Load into a brand-new editor draft so the user can review before publishing.
      setSelected(null);
      setDraft({
        slug: "",
        title: data.title || "",
        excerpt: data.excerpt || "",
        cover_image: "",
        content_md: data.content_md || "",
        related_product_slugs:
          data.suggested_related_product_slugs?.length
            ? data.suggested_related_product_slugs
            : draft.related_product_slugs,
        published: false, // safer default — admin reviews then publishes
        publish_at: "",
      });
      setShowPreview(true);
      setAiOpen(false);
      toast.success("AI draft loaded — review then save");
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "AI generation failed");
    } finally {
      setAiBusy(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="admin-articles-panel">
      {/* View toggle: Editor (sidebar + editor) vs Calendar (month grid) */}
      <div className="flex items-center gap-2">
        <div className="brut bg-white p-1 inline-flex">
          <button
            type="button"
            onClick={() => setView("editor")}
            data-testid="articles-view-editor"
            className={`px-3 py-1.5 font-mono uppercase text-[10px] tracking-widest inline-flex items-center gap-1.5 ${
              view === "editor" ? "pcs-bg-pink text-white" : "bg-white"
            }`}
          >
            <List className="w-3 h-3" strokeWidth={3} />
            Editor
          </button>
          <button
            type="button"
            onClick={() => setView("calendar")}
            data-testid="articles-view-calendar"
            className={`px-3 py-1.5 font-mono uppercase text-[10px] tracking-widest inline-flex items-center gap-1.5 ${
              view === "calendar" ? "pcs-bg-pink text-white" : "bg-white"
            }`}
          >
            <Calendar className="w-3 h-3" strokeWidth={3} />
            Calendar
          </button>
        </div>
        {view === "calendar" && (
          <button
            type="button"
            onClick={() => {
              setView("editor");
              startNew();
            }}
            data-testid="calendar-new-article-btn"
            className="brut bg-white px-3 py-1.5 font-mono uppercase text-[10px] tracking-widest brut-press inline-flex items-center gap-1.5"
          >
            <Plus className="w-3 h-3" strokeWidth={3} /> New article
          </button>
        )}
        {view === "calendar" && (
          <button
            type="button"
            onClick={() => setAiOpen(true)}
            data-testid="calendar-ai-draft-btn"
            className="brut pcs-bg-blue text-white px-3 py-1.5 font-mono uppercase text-[10px] tracking-widest brut-press inline-flex items-center gap-1.5"
          >
            <Sparkles className="w-3 h-3" strokeWidth={3} /> Draft with AI
          </button>
        )}
      </div>

      {view === "calendar" ? (
        <ArticleCalendar
          articles={articles}
          onSelectArticle={(slug) => {
            setView("editor");
            openArticle(slug);
          }}
          onCreateNew={() => {
            setView("editor");
            startNew();
          }}
        />
      ) : (
        renderArticlesEditorView()
      )}

      {/* AI Draft modal — shared between editor and calendar views */}
      {aiOpen && renderAiDraftModal()}
    </div>
  );

  // ----- Inline JSX helpers (NOT React components — called as plain functions
  // so React reconciles their children directly into the parent tree and we
  // avoid the "component defined inside parent" remount/state-loss footgun). -----

  function renderAiDraftModal() {
    return (
      <div
        className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
        onClick={() => !aiBusy && setAiOpen(false)}
        data-testid="ai-draft-modal"
      >
        <div
          className="brut bg-white w-full max-w-2xl p-5 space-y-4"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-start justify-between gap-3 pb-3 border-b-2 border-black">
            <div>
              <div className="font-display text-2xl tracking-tight inline-flex items-center gap-2">
                <Sparkles className="w-5 h-5 pcs-text-pink" strokeWidth={3} />
                Draft with AI
              </div>
              <p className="text-xs text-gray-700 mt-1">
                Claude Sonnet 4.5 will write a long-form, SEO-friendly article and pick
                relevant products from your catalog. You review &amp; edit before publishing.
              </p>
            </div>
            <button
              type="button"
              onClick={() => !aiBusy && setAiOpen(false)}
              disabled={aiBusy}
              className="brut bg-white p-1.5 brut-press disabled:opacity-50"
              data-testid="ai-draft-close"
            >
              <X className="w-4 h-4" strokeWidth={3} />
            </button>
          </div>

          <div>
            <label className="font-mono uppercase text-[10px] tracking-widest opacity-70 block mb-1">
              Topic *
            </label>
            <input
              value={aiTopic}
              onChange={(e) => setAiTopic(e.target.value)}
              placeholder="e.g. 'Top 10 Halloween couples costumes for 2026'"
              data-testid="ai-topic-input"
              className="w-full brut bg-white px-3 py-2 focus:outline-none"
              autoFocus
            />
          </div>

          <div>
            <label className="font-mono uppercase text-[10px] tracking-widest opacity-70 block mb-1">
              SEO keywords (comma-separated, optional)
            </label>
            <input
              value={aiKeywords}
              onChange={(e) => setAiKeywords(e.target.value)}
              placeholder="halloween couples costumes, easy costume ideas, 2026 trends"
              data-testid="ai-keywords-input"
              className="w-full brut bg-white px-3 py-2 focus:outline-none font-mono text-xs"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="font-mono uppercase text-[10px] tracking-widest opacity-70 block mb-1">
                Tone
              </label>
              <input
                value={aiTone}
                onChange={(e) => setAiTone(e.target.value)}
                data-testid="ai-tone-input"
                className="w-full brut bg-white px-3 py-2 focus:outline-none text-sm"
              />
            </div>
            <div>
              <label className="font-mono uppercase text-[10px] tracking-widest opacity-70 block mb-1">
                Target words
              </label>
              <input
                type="number"
                min={300}
                max={2500}
                step={100}
                value={aiTargetWords}
                onChange={(e) => setAiTargetWords(e.target.value)}
                data-testid="ai-words-input"
                className="w-full brut bg-white px-3 py-2 focus:outline-none text-sm font-mono"
              />
            </div>
          </div>

          {draft.related_product_slugs.length > 0 && (
            <div className="brut pcs-bg-cream p-3 text-xs">
              <span className="font-mono uppercase text-[10px] tracking-widest opacity-70">
                Seeded with {draft.related_product_slugs.length} pre-selected product
                {draft.related_product_slugs.length === 1 ? "" : "s"}
              </span>{" "}
              — the AI will weave them into the article and may suggest more.
            </div>
          )}

          <div className="flex items-center justify-end gap-2 pt-2 border-t-2 border-black">
            <button
              type="button"
              onClick={() => setAiOpen(false)}
              disabled={aiBusy}
              className="brut bg-white px-3 py-2 font-bold uppercase text-xs tracking-wide brut-press disabled:opacity-50"
              data-testid="ai-draft-cancel"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => {
                setView("editor");
                generateWithAi();
              }}
              disabled={aiBusy || !aiTopic.trim()}
              className="brut pcs-bg-pink text-white px-4 py-2 font-bold uppercase text-xs tracking-wide brut-press disabled:opacity-50 inline-flex items-center gap-2"
              data-testid="ai-draft-generate"
            >
              <Sparkles className="w-3.5 h-3.5" strokeWidth={3} />
              {aiBusy ? "Drafting... (~30s)" : "Generate draft"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  function renderArticlesEditorView() {
    return (
    <div className="grid lg:grid-cols-[280px_1fr] gap-4">
      {/* Sidebar list */}
      <aside className="space-y-2">
        <button
          type="button"
          onClick={startNew}
          data-testid="article-new-btn"
          className={`w-full brut px-3 py-3 font-bold uppercase tracking-wide text-sm brut-press inline-flex items-center justify-center gap-2 ${
            isNew ? "pcs-bg-pink text-white" : "bg-white"
          }`}
        >
          <Plus className="w-4 h-4" strokeWidth={3} />
          New article
        </button>

        <button
          type="button"
          onClick={() => setAiOpen(true)}
          data-testid="article-ai-open-btn"
          className="w-full brut pcs-bg-blue text-white px-3 py-3 font-bold uppercase tracking-wide text-sm brut-press inline-flex items-center justify-center gap-2"
        >
          <Sparkles className="w-4 h-4" strokeWidth={3} />
          Draft with AI
        </button>

        <div className="brut bg-white max-h-[60vh] overflow-y-auto">
          {loading ? (
            <div className="p-4 font-mono uppercase text-xs tracking-widest opacity-70">
              Loading...
            </div>
          ) : articles.length === 0 ? (
            <div className="p-4 text-sm text-gray-700">No articles yet. Create your first one.</div>
          ) : (
            <ul className="divide-y-2 divide-black/10">
              {articles.map((a) => (
                <li key={a.slug}>
                  <button
                    type="button"
                    onClick={() => openArticle(a.slug)}
                    data-testid={`article-list-${a.slug}`}
                    className={`w-full text-left p-3 hover:pcs-bg-cream transition-colors ${
                      selected === a.slug ? "pcs-bg-cream" : ""
                    }`}
                  >
                    <div className="font-bold text-sm line-clamp-2 leading-tight">{a.title}</div>
                    <div className="mt-1 flex items-center gap-2 font-mono uppercase text-[10px] tracking-widest opacity-70">
                      {(() => {
                        const s = articleStatus(a);
                        return <span className={`px-1.5 ${s.cls}`}>{s.label}</span>;
                      })()}
                      <span className="truncate">/{a.slug}</span>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </aside>

      {/* Editor */}
      <div className="brut bg-white p-4 md:p-5 space-y-4" data-testid="article-editor">
        {/* Header bar */}
        <div className="flex flex-wrap items-center gap-2 pb-3 border-b-2 border-black">
          <div className="font-display text-2xl tracking-tight">
            {isNew ? "New article" : "Editing"}
          </div>
          {!isNew && original && (
            <Link
              to={`/article/${original.slug}`}
              target="_blank"
              rel="noopener noreferrer"
              className="brut bg-white px-2 py-1 font-mono uppercase text-[10px] tracking-widest brut-press inline-flex items-center gap-1"
              data-testid="article-view-live"
            >
              <ExternalLink className="w-3 h-3" />
              View live
            </Link>
          )}
          <button
            type="button"
            onClick={() => setShowPreview((p) => !p)}
            data-testid="article-toggle-preview"
            className={`brut px-2 py-1 font-mono uppercase text-[10px] tracking-widest brut-press inline-flex items-center gap-1 ${
              showPreview ? "pcs-bg-blue text-white" : "bg-white"
            }`}
          >
            {showPreview ? <Edit3 className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
            {showPreview ? "Edit" : "Preview"}
          </button>

          <div className="ml-auto flex items-center gap-2">
            <label className="inline-flex items-center gap-1.5 font-mono uppercase text-[10px] tracking-widest cursor-pointer">
              <input
                type="checkbox"
                checked={draft.published}
                onChange={togglePublished}
                data-testid="article-published-toggle"
                className="w-4 h-4 border-2 border-black"
              />
              Published
            </label>
            {!isNew && (
              <button
                type="button"
                onClick={del}
                disabled={busy}
                data-testid="article-delete"
                className="brut bg-white px-2 py-1 font-mono uppercase text-[10px] tracking-widest brut-press disabled:opacity-50 inline-flex items-center gap-1"
              >
                <Trash2 className="w-3 h-3" />
                Delete
              </button>
            )}
            <button
              type="button"
              onClick={save}
              disabled={busy || !dirty}
              data-testid="article-save"
              className="brut pcs-bg-pink text-white px-3 py-1.5 font-bold uppercase text-xs tracking-wide brut-press disabled:opacity-50"
            >
              {(() => {
                if (busy) return "Saving...";
                const scheduledAhead =
                  draft.publish_at &&
                  new Date(draft.publish_at).getTime() > Date.now() &&
                  draft.published;
                if (isNew) return scheduledAhead ? "Schedule" : "Publish";
                if (!dirty) return "Saved";
                return scheduledAhead ? "Save & schedule" : "Save changes";
              })()}
            </button>
          </div>
        </div>

        {/* Schedule row */}
        <div
          className="flex flex-wrap items-center gap-2 -mt-1"
          data-testid="article-schedule-row"
        >
          <Clock className="w-4 h-4 opacity-70" strokeWidth={2.5} />
          <label className="font-mono uppercase text-[10px] tracking-widest opacity-70">
            Schedule for:
          </label>
          <input
            type="datetime-local"
            value={draft.publish_at || ""}
            onChange={(e) => setDraft({ ...draft, publish_at: e.target.value })}
            data-testid="article-publish-at"
            className="brut bg-white px-2 py-1 font-mono text-xs focus:outline-none"
          />
          {draft.publish_at && (
            <button
              type="button"
              onClick={() => setDraft({ ...draft, publish_at: "" })}
              data-testid="article-clear-schedule"
              className="font-mono uppercase text-[10px] tracking-widest opacity-70 hover:opacity-100 underline"
            >
              clear
            </button>
          )}
          {draft.publish_at && draft.published && (
            <span className="font-mono uppercase text-[10px] tracking-widest pcs-bg-blue text-white px-1.5">
              {new Date(draft.publish_at).getTime() > Date.now()
                ? `goes live ${new Date(draft.publish_at).toLocaleString()}`
                : `was scheduled (now live)`}
            </span>
          )}
          {!draft.published && draft.publish_at && (
            <span className="font-mono uppercase text-[10px] tracking-widest opacity-60">
              (also requires Published to be checked)
            </span>
          )}
        </div>

        {/* Title + slug */}
        <div>
          <input
            value={draft.title}
            onChange={(e) => setDraft({ ...draft, title: e.target.value })}
            placeholder="Article title"
            data-testid="article-title"
            className="w-full font-display text-2xl md:text-3xl tracking-tight px-3 py-2 brut bg-white focus:outline-none"
          />
          <div className="mt-1 flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest opacity-70">
            <span>slug:</span>
            <input
              value={draft.slug || slugifyClient(draft.title)}
              onChange={(e) => setDraft({ ...draft, slug: slugifyClient(e.target.value) })}
              data-testid="article-slug"
              className="flex-1 bg-transparent border-b-2 border-black/30 focus:outline-none focus:border-black px-1 font-mono text-[11px]"
            />
            {draft.slug && (
              <span>
                → /article/{draft.slug || slugifyClient(draft.title)}
              </span>
            )}
          </div>
        </div>

        {/* Excerpt */}
        <div>
          <label className="font-mono uppercase text-[10px] tracking-widest opacity-70 block mb-1">
            Excerpt (one-line preview shown on the blog index and OG / social cards)
          </label>
          <textarea
            value={draft.excerpt}
            onChange={(e) => setDraft({ ...draft, excerpt: e.target.value })}
            rows={2}
            placeholder="Short, punchy sentence that makes someone click."
            data-testid="article-excerpt"
            className="w-full brut bg-white px-3 py-2 focus:outline-none resize-y"
          />
        </div>

        {/* Cover image */}
        <div>
          <ImageUploader
            label="Cover image"
            value={draft.cover_image}
            onChange={(url) => setDraft({ ...draft, cover_image: url })}
            testId="article-cover"
            aspect="aspect-[16/9]"
          />
        </div>

        {/* Content — editor or preview */}
        <div>
          <label className="font-mono uppercase text-[10px] tracking-widest opacity-70 block mb-1">
            Content (Markdown — supports **bold**, *italic*, # headings, - lists, links, tables)
          </label>
          {showPreview ? (
            <div
              className="brut bg-white p-5 prose-pcs min-h-[400px]"
              data-testid="article-preview"
            >
              <ReactMarkdown remarkPlugins={REMARK_PLUGINS}>
                {draft.content_md || "*Nothing to preview yet.*"}
              </ReactMarkdown>
            </div>
          ) : (
            <textarea
              value={draft.content_md}
              onChange={(e) => setDraft({ ...draft, content_md: e.target.value })}
              rows={20}
              data-testid="article-content"
              className="w-full brut bg-white px-3 py-2 focus:outline-none font-mono text-sm resize-y"
              style={{ minHeight: "400px" }}
            />
          )}
          <div className="mt-1 flex items-center justify-between font-mono text-[10px] uppercase tracking-widest opacity-60">
            <span>{draft.content_md.length} chars · ~{Math.round(draft.content_md.split(/\s+/).filter(Boolean).length / 230)} min read</span>
            <a
              href="https://www.markdownguide.org/cheat-sheet/"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:pcs-text-pink"
            >
              Markdown cheatsheet ↗
            </a>
          </div>
        </div>

        {/* Related products */}
        <div>
          <label className="font-mono uppercase text-[10px] tracking-widest opacity-70 block mb-1">
            Related products ({draft.related_product_slugs.length} selected — appears at the bottom of the article)
          </label>
          <input
            value={productSearch}
            onChange={(e) => setProductSearch(e.target.value)}
            placeholder="Search products to add..."
            data-testid="article-products-search"
            className="w-full brut bg-white px-3 py-2 focus:outline-none mb-2"
          />
          <div className="brut bg-white p-2 max-h-[200px] overflow-y-auto">
            {filteredProducts.length === 0 ? (
              <div className="p-2 text-sm text-gray-700">No products match.</div>
            ) : (
              <ul className="space-y-1">
                {filteredProducts.slice(0, 50).map((p) => {
                  const selected = draft.related_product_slugs.includes(p.slug);
                  return (
                    <li key={p.slug}>
                      <button
                        type="button"
                        onClick={() => toggleRelated(p.slug)}
                        data-testid={`article-product-${p.slug}`}
                        className={`w-full text-left px-2 py-1.5 text-sm rounded transition-colors flex items-center gap-2 ${
                          selected ? "pcs-bg-blue text-white" : "hover:bg-gray-100"
                        }`}
                      >
                        <span className="font-mono text-[10px] uppercase opacity-70 min-w-[50px]">
                          {p.vendor}
                        </span>
                        <span className="truncate flex-1">{p.title}</span>
                        <span className="font-mono text-[10px] opacity-70">
                          {selected ? "✓" : "+"}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      </div>

      {/* AI Draft modal is rendered by the top-level <AiDraftModal /> so it
          shows in both Editor and Calendar views. */}
    </div>
  );
  }
}
